<script setup lang="ts">
import {ref, computed, onMounted, inject, watch} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { accountService } from '@/services/api';
import PageSpinner from '@/components/v2/PageSpinner.vue';
import { Account } from '@/types/Account';
import { cyberCubeService } from '@/services/api';
import { triSeriesColors } from '@/utilities/Color';
import { AnalysisRun, AnalysisRunStatus, CurrentVSAggregateExposure } from '@/types/CyberCube';
import { Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { ChevronLeftIcon, TriangleAlert } from '@lucide/vue';
import { FusionChartMSArea2D } from '@/types/MultiSeries.js';
import { toCurrency } from '@/utilities/Number.js';
import AppSelect from '@/components/v2/AppSelect.vue';

// Data
// ----

const route = useRoute();
const router = useRouter();
const REFRESH_INTERVAL = 10000;
const MAX_POLL_COUNT = 12;
const DEFAULT_PERCENTILE = 95;

const currentVsAggregateExposureChart = {
  caption: 'Current Limits and Aggregate Financial Exposure',
  theme: 'cacs',
  decimals: 1,
  xAxisName: 'Security Maturity',
  exportFilename: 'Current vs Aggregate Financial Exposure',
  paletteColors: triSeriesColors,
  plotColorinTooltip: 0,
  plotToolText: '$toolText',
  valueFontAlpha: 0,
  drawAnchors: 1,
};

const currentVsAggregateExposureCategories = [
  'Initial',
  '',
  'Repeatable',
  '',
  'Defined',
  '',
  'Managed',
  '',
  'Optimized',
];

// Refs
// ----

const account = ref<Account | null>(null);
const loading = ref(false);
const validOwnershipType = ref(true);
const errors = ref<any[]>([]);
const currentVsAggregateExposureResults = ref<CurrentVSAggregateExposure[] | null>(null);
const selectedPercentile = ref<number | null>(null);
const cyberCubeUnavailable = ref(false);
const companyAnalysisPollCount = ref(0);
const analysisRun = ref<AnalysisRun | null>(null);
const selectedCategoryType = ref<number | null | undefined>(inject('selectedCategoryType'));

// Computed
// --------

const hasErrors = computed(() => errors.value.length > 0);
const hasCyberCubeAccount = computed(() => account.value?.cyberCubeID);

const hasAnalysis = computed(() => analysisRun.value?.status === AnalysisRunStatus.SUCCESS);
const analysisFailed = computed(() => analysisRun.value?.status === AnalysisRunStatus.FAILED);
const analysisRunning = computed(
  () =>
    analysisRun.value?.status === AnalysisRunStatus.RUNNING || analysisRun.value?.status === AnalysisRunStatus.QUEUED,
);

const percentiles = computed(() => {
  if (currentVsAggregateExposureResults.value) {
    return currentVsAggregateExposureResults.value.map((result) => result.percentile).sort((a, b) => a - b);
  }
  return [];
});

const editRoute = computed(() => {
    return route.path.includes('/account')
    ? `/account/${account.value?.accountID}/analytics/category/${selectedCategoryType.value}/cyber/edit`
    : `/analytics/category/${selectedCategoryType.value}/cyber/${account.value?.accountID}/edit`;
})

const placeholderText = computed(() => {
  if (cyberCubeUnavailable.value) {
    return 'CyberCube is currently unavailable';
  } else if (companyAnalysisPollCount.value > MAX_POLL_COUNT) {
    return 'Analysis has timed out, please try again';
  } else if (analysisRunning.value) {
    return 'Analysis is running, please allow up to 1 minute for the results to appear';
  } else if (analysisFailed.value) {
    return 'Analysis has failed, please try again';
  } else {
    return 'Run an Analysis to view the Aggregate Financial Exposure';
  }
});

const currentVsAggregateExposure = computed<FusionChartMSArea2D>(() => {
  return {
    chart: { ...currentVsAggregateExposureChart },
    categories: [{ category: currentVsAggregateExposureCategories.map((item) => ({ label: item })) }],
    dataset: currentVsAggregateExposureData.value,
  };
});

const currentVsAggregateExposureData = computed(() => {
  if (currentVsAggregateExposureResults.value && selectedPercentile.value) {
    const results = currentVsAggregateExposureResults.value.find(
      (result) => result.percentile === selectedPercentile.value,
    );
    if (!results) {
      return [];
    }

    const currentLimit = results.currentLimit;
    const dataPoints = [
      results.point1,
      results.point2,
      results.point3,
      results.point4,
      results.point5,
      results.point6,
      results.point7,
      results.point8,
      results.point9,
      results.matureLoss,
    ];
    return [
      {
        seriesname: 'Potential Loss',
        data: dataPoints.map((point) => ({
          value: point,
          toolText: `Potential Loss Impact: ${toCurrency(point)}`,
        })),
      },
      {
        seriesname: 'Current Limits',
        showToolTip: 0,
        data: dataPoints.map((point) => ({
          value: currentLimit,
          toolText: `Risk Coverage Gap: ${toCurrency(point - currentLimit)}`,
        })),
      },
    ];
  }
  return [];
});

// Methods
// -------

function onBackButtonClick() {
  if (selectedCategoryType.value) {
    return route.path.includes('/account')
        ? router.push(`/account/${account.value?.accountID}/analytics/category/${selectedCategoryType.value}`)
        : router.push(`/analytics/category/${selectedCategoryType.value}`);
  } else {
    return route.path.includes('/account')
        ? router.push(`/account/${account.value?.accountID}/analytics`)
        : router.push('/analytics');
  }
}

async function runCompanyExposureAnalysis() {
  companyAnalysisPollCount.value = 0;
  try {
    if (account.value?.accountID && account.value?.cyberCubeID) {
      await cyberCubeService.startExposureAnalysis(account.value?.accountID, account.value?.cyberCubeID as string);

      // wait a second for the status record to be written
      await new Promise((resolve) => setTimeout(resolve, 1000));

      await getLatestExposureAnalysisRun();
      await pollExposureAnalysisResults();
    }
  } catch (e: any) {
    if (e.response?.status === 429 || e.cause?.status === 429) {
      cyberCubeUnavailable.value = true;
      Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'CyberCube is not available', color: 'error' });
    }
    console.error(e);
  }
}

async function getLatestExposureAnalysisRun() {
  if (account.value?.accountID) {
    const analysisResponse = await cyberCubeService.getLatestExposureAnalysisRun(account.value?.accountID);
    if (analysisResponse && analysisResponse.length > 0) {
      analysisRun.value = analysisResponse[0];
    }
  }
}

async function getCurrentVSAggregateExposure() {
  const accountId = account.value?.accountID;
  if (!accountId) {
    return;
  }

  const results = await cyberCubeService.getCurrentVSAggregateExposure(accountId);
  if (!results || results.length === 0) {
    return;
  }

  const { currentLimit } = results[0];
  if (!currentLimit) {
    selectedPercentile.value = DEFAULT_PERCENTILE;
  } else {
    // Find the smallest point9 value that is still above the current limit
    const filteredResults = results.filter((item) => item.point9 > currentLimit);
    filteredResults.sort((a, b) => a.point9 - b.point9);
    selectedPercentile.value = filteredResults[0]?.percentile ?? DEFAULT_PERCENTILE;
  }

  currentVsAggregateExposureResults.value = results;
}

async function pollExposureAnalysisResults() {
  const accountId = account.value?.accountID;
  const cyberCubeId = account.value?.cyberCubeID;

  if (accountId && cyberCubeId) {
    try {
      if (hasAnalysis.value) {
        return;
      } else if (companyAnalysisPollCount.value <= MAX_POLL_COUNT && analysisRunning.value) {
        companyAnalysisPollCount.value += 1;
        setTimeout(async () => {
          await getLatestExposureAnalysisRun();
          await pollExposureAnalysisResults();
        }, REFRESH_INTERVAL);
      }
    } catch (e: any) {
      if (e.response?.status === 429 || e.cause?.status === 429) {
        cyberCubeUnavailable.value = true;
        Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'CyberCube is not available', color: 'error' });
      }
      console.error(e);
    }
  }
}

async function refresh() {
  loading.value = true;
  errors.value = [];
  try {
    account.value = await accountService.findById(route.params.id as string);
    validOwnershipType.value = account.value?.ownershipType !== undefined;
    await getLatestExposureAnalysisRun();

    if (hasAnalysis.value) {
      await getCurrentVSAggregateExposure();
    } else if (!analysisRunning.value && (!hasAnalysis.value || analysisFailed.value)) {
      await runCompanyExposureAnalysis();
    } else if (analysisRunning.value) {
      await pollExposureAnalysisResults();
    }
  } catch (error) {
    errors.value.push(error);
  } finally {
    loading.value = false;
  }
}

async function loadResult<T>(fn: () => Promise<T>): Promise<T> {
  loading.value = true;
  try {
    return await fn();
  } finally {
    loading.value = false;
  }
}

// ---------
// watchers
// ---------

watch(analysisRunning, async (isRunning, wasRunning) => {
    if (wasRunning && !isRunning && hasAnalysis.value) {
        // Analysis completed, fetch the results
        await getCurrentVSAggregateExposure();
    }
});

// ---------
// Lifecycle
// ---------

onMounted(async () => {
  await refresh();
});
</script>

<template>
  <div class="dashboard">
    <PageSpinner v-if="loading" />

    <div v-else>
      <div class="header px-4 pt-4">
        <h1>{{ account?.accountName }} CyberCube Analysis</h1>
        <div class="spacer" />
      </div>

      <div class="divider m-0 w-full mx-auto"></div>
      <div class="flex items-center gap-3 navigation">
        <div
            class="btn btn-sm flex items-center justify-items-start gap-1"
            @click="onBackButtonClick">
          <ChevronLeftIcon :size="15" />
          <span class="mr-1">Back</span>
        </div>
        <router-link class="btn btn-sm" :to="editRoute">
          <span class="">Edit Model</span>
        </router-link>
      </div>
    </div>

    <div class="inner" v-if="!loading && !hasErrors && validOwnershipType">
      <div class="flex w-full items-center justify-end gap-4">
        <div>
          <button
            v-if="hasCyberCubeAccount && analysisFailed"
            class="btn btn-primary btn-sm flex"
            :class="{ 'btn-disabled': cyberCubeUnavailable }"
            @click="loadResult(runCompanyExposureAnalysis)">
            Retry Analysis
          </button>
        </div>
      </div>
      <div v-if="cyberCubeUnavailable" class="alert alert-warning mx-auto w-80">
        <TriangleAlert />
        <span class="flex w-full justify-center">CyberCube is currently unavailable</span>
      </div>

      <div v-if="hasCyberCubeAccount">
        <div v-if="hasAnalysis && currentVsAggregateExposureResults">
          <div class="mx-auto mt-4 max-w-[1000px]">
            <div class="mb-4 mr-3 flex items-center justify-end gap-3">
              <span class="text-sm">Breach Severity:</span>
              <AppSelect
                v-model="selectedPercentile"
                :options="percentiles.map(p => ({ value: p, label: `${p}th` }))"
                class="w-[120px] h-9"
                placeholder="Select percentile"
              />
            </div>

            <fusioncharts
              type="msarea"
              width="100%"
              height="400"
              dataFormat="json"
              :dataSource="currentVsAggregateExposure" />
          </div>
        </div>

        <div v-else class="mt-28 flex w-full justify-center">
          <span class="text-gray-600">{{ placeholderText }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dashboard {
  padding: 10px;
  width: -webkit-fill-available;
}

.header {
  display: grid;
  grid-template-columns: auto 1fr auto;
  padding-bottom: 0.5rem;
  align-content: center;
  align-items: center;
}

.header > h1 {
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;
}

.header > .spacer {
  padding: 0 1rem;
}

.navigation{
  padding: 10px;
}
</style>
